import { useEffect, useRef, useState } from "react";
import { siteRequest, operationKey } from "lib/siteCustomerApi";
export const date = (value) =>
  value
    ? new Date(value._seconds ? value._seconds * 1000 : value).toLocaleString()
    : "—";
export const bytes = (value) =>
  value < 1024 * 1024
    ? `${Math.max(1, Math.round(value / 1024))} KB`
    : `${(value / (1024 * 1024)).toFixed(1)} MB`;
export function Notice({ error }) {
  return error ? (
    <div className="ca-error" role="alert">
      <p>{error.message}</p>
      {error.requestId && <small>Reference: {error.requestId}</small>}
    </div>
  ) : null;
}
export function useRemote(path, tick = 0) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    const abort = new AbortController();
    setState({ loading: true });
    siteRequest(path, { signal: abort.signal })
      .then((result) => setState({ data: result.data }))
      .catch((error) => {
        if (!abort.signal.aborted) setState({ error });
      });
    return () => abort.abort();
  }, [path, tick]);
  return state;
}
export function Remote({ state, children }) {
  return state.loading ? (
    <p role="status">Loading…</p>
  ) : state.error ? (
    <Notice error={state.error} />
  ) : (
    children
  );
}
export function Pager({ next, pages, setPages }) {
  return (
    <div className="ca-actions ca-pager">
      <button
        disabled={pages.length === 1}
        onClick={() => setPages((p) => p.slice(0, -1))}
      >
        Previous
      </button>
      <span>Page {pages.length}</span>
      <button disabled={!next} onClick={() => setPages((p) => [...p, next])}>
        Next
      </button>
    </div>
  );
}
export function useOperation() {
  const retry = useRef(null),
    flight = useRef(false),
    alive = useRef(true);
  const [state, setState] = useState({});
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const run = async (path, body, method = "POST") => {
    if (flight.current) return null;
    const fingerprint = JSON.stringify([path, body, method]);
    if (retry.current?.fingerprint !== fingerprint)
      retry.current = { fingerprint, key: operationKey() };
    flight.current = true;
    setState({ busy: true });
    try {
      const result = await siteRequest(path, {
        method,
        body,
        key: retry.current.key,
      });
      retry.current = null;
      if (alive.current) setState({ receipt: result.requestId });
      return result.data;
    } catch (error) {
      if (alive.current) setState({ error });
      return null;
    } finally {
      flight.current = false;
    }
  };
  return { ...state, run };
}
export function Receipt({ op, children = "Saved." }) {
  return (
    <>
      <Notice error={op.error} />
      {op.receipt && (
        <p className="ca-notice" role="status">
          {children}
          <small>Reference: {op.receipt}</small>
        </p>
      )}
    </>
  );
}
